import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { apiClient } from '../../lib/api'
import LoadingSpinner from '../ui/LoadingSpinner'

interface OAuthAccount {
  provider: string
  provider_user_id: string
  email: string
  display_name: string
  linked_at: string
}

interface OAuthAccountsResponse {
  success: boolean
  data: {
    accounts: OAuthAccount[]
  }
}

interface DisconnectModalProps {
  provider: string
  email: string
  onConfirm: () => void
  onCancel: () => void
}

const DisconnectModal: React.FC<DisconnectModalProps> = ({ provider, email, onConfirm, onCancel }) => {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onCancel} />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Disconnect {provider.charAt(0).toUpperCase() + provider.slice(1)} Account
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  Are you sure you want to disconnect <span className="font-medium">{email}</span>?
                </p>
                <p className="mt-2 text-sm text-gray-500">
                  You'll need to enter your password to log in if you disconnect this account.
                </p>
              </div>
            </div>
          </div>
          <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onConfirm}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Disconnect
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

const ConnectedAccounts: React.FC = () => {
  const queryClient = useQueryClient()
  const [disconnectAccount, setDisconnectAccount] = useState<{ provider: string; email: string } | null>(null)

  // Fetch connected OAuth accounts
  const { data: accountsData, isLoading } = useQuery<OAuthAccountsResponse>({
    queryKey: ['oauth-accounts'],
    queryFn: async () => {
      const response = await apiClient.get('/oauth/accounts')
      return response.data
    }
  })

  // Check if user has a password set
  const { data: passwordStatus } = useQuery<{ has_password: boolean }>({
    queryKey: ['user-password-status'],
    queryFn: async () => {
      const response = await apiClient.get('/oauth/password-status')
      return response.data.data
    }
  })

  // Disconnect OAuth account mutation
  const disconnectMutation = useMutation({
    mutationFn: async (provider: string) => {
      const response = await apiClient.delete(`/oauth/unlink/${provider}`)
      return response.data
    },
    onSuccess: () => {
      toast.success('Account disconnected successfully')
      queryClient.invalidateQueries({ queryKey: ['oauth-accounts'] })
      setDisconnectAccount(null)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to disconnect account')
    }
  })

  const handleDisconnect = (provider: string, email: string) => {
    // Check if user has password before allowing disconnect
    if (!passwordStatus?.has_password) {
      toast.error('You must set a password before disconnecting your OAuth account')
      return
    }

    setDisconnectAccount({ provider, email })
  }

  const confirmDisconnect = () => {
    if (disconnectAccount) {
      disconnectMutation.mutate(disconnectAccount.provider)
    }
  }

  const getProviderIcon = (provider: string) => {
    if (provider === 'google') {
      return (
        <svg className="w-8 h-8" viewBox="0 0 24 24">
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
      )
    }
    return null
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <LoadingSpinner />
      </div>
    )
  }

  const accounts = accountsData?.accounts || []

  // Debug logging
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium text-gray-900">Connected Accounts</h3>
        <p className="mt-1 text-sm text-gray-500">
          Manage your OAuth authentication providers
        </p>
      </div>

      {accounts.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 10V3L4 14h7v7l9-11h-7z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No connected accounts</h3>
          <p className="mt-1 text-sm text-gray-500">
            You haven't connected any OAuth providers yet.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {accounts.map((account) => (
            <div
              key={account.provider}
              className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between hover:border-gray-300 transition-colors"
            >
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  {getProviderIcon(account.provider)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">
                    {account.provider.charAt(0).toUpperCase() + account.provider.slice(1)}
                  </p>
                  <p className="text-sm text-gray-500 truncate">
                    {account.email}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Connected on {formatDate(account.linked_at)}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Connected
                  </span>
                </div>
              </div>
              <div className="ml-4">
                <button
                  onClick={() => handleDisconnect(account.provider, account.email)}
                  disabled={disconnectMutation.isPending}
                  className="inline-flex items-center px-3 py-1.5 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {disconnectMutation.isPending ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Disconnecting...
                    </>
                  ) : (
                    'Disconnect'
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!passwordStatus?.has_password && accounts.length > 0 && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                You should set a password to ensure you can still access your account if you disconnect OAuth authentication.
              </p>
            </div>
          </div>
        </div>
      )}

      {disconnectAccount && (
        <DisconnectModal
          provider={disconnectAccount.provider}
          email={disconnectAccount.email}
          onConfirm={confirmDisconnect}
          onCancel={() => setDisconnectAccount(null)}
        />
      )}
    </div>
  )
}

export default ConnectedAccounts
